'use client';

import { VoteType } from '@prisma/client';
import { AnimatePresence } from 'framer-motion';
import { useSession } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { InContentBanner } from '../../../components/banner/InContentBanner';
import { PlaceSeed } from '../../../types/shared';
import { deleteTourismPlace, voteOnPlace } from '../lib/actions/tourismActions';
import { PlaceCard } from './PlaceCard';
import { ItemViewModal } from './shared/ItemViewModal';

function applyOptimisticVote(place: PlaceSeed, voteType: VoteType): PlaceSeed {
  const prev = place.myVote ?? null;
  let likes = place.initialLikes ?? 0;
  let dislikes = place.initialDislikes ?? 0;

  if (prev === voteType) {
    if (voteType === VoteType.LIKE) likes = Math.max(0, likes - 1);
    if (voteType === VoteType.DISLIKE) dislikes = Math.max(0, dislikes - 1);
    return { ...place, myVote: null, initialLikes: likes, initialDislikes: dislikes };
  }

  if (prev === VoteType.LIKE) likes = Math.max(0, likes - 1);
  if (prev === VoteType.DISLIKE) dislikes = Math.max(0, dislikes - 1);

  if (voteType === VoteType.LIKE) likes += 1;
  if (voteType === VoteType.DISLIKE) dislikes += 1;

  return { ...place, myVote: voteType, initialLikes: likes, initialDislikes: dislikes };
}

export function TourismClientTab({
  places: initialPlaces,
  initialActivePlace,
}: {
  places: PlaceSeed[];
  baseUrl: string;
  initialActivePlace: PlaceSeed | null;
}) {
  const [places, setPlaces] = useState<PlaceSeed[]>(initialPlaces);

  // ✅ активная карточка: id + fallback (если place открыт по ссылке и не на странице)
  const [activePlaceId, setActivePlaceId] = useState<string | null>(initialActivePlace?.id ?? null);
  const [activeFallback, setActiveFallback] = useState<PlaceSeed | null>(() => {
    if (!initialActivePlace) return null;
    const inList = initialPlaces.some((p) => p.id === initialActivePlace.id);
    return inList ? null : initialActivePlace;
  });

  const activePlace = useMemo(() => {
    if (!activePlaceId) return null;
    return places.find((p) => p.id === activePlaceId) ?? activeFallback;
  }, [activePlaceId, places, activeFallback]);

  const { data: session } = useSession();
  const userId = session?.user?.id;
  const isAdmin = session?.user?.role === 'ADMIN';

  const searchParams = useSearchParams();
  const commentIdToScroll = searchParams.get('commentId');

  useEffect(() => {
    setPlaces(initialPlaces);
    // если у нас был fallback и он внезапно появился в списке — убираем fallback
    if (activeFallback && initialPlaces.some((p) => p.id === activeFallback.id)) {
      setActiveFallback(null);
    }
  }, [initialPlaces]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeletePlace = useCallback(async (placeId: string) => {
    const promise = deleteTourismPlace(placeId).then((result) => {
      if (!result.success) throw new Error(result.message);

      setPlaces((current) => current.filter((p) => p.id !== placeId));
      setActivePlaceId((cur) => (cur === placeId ? null : cur));
      setActiveFallback((cur) => (cur?.id === placeId ? null : cur));

      return result.message;
    });

    toast.promise(promise, {
      loading: 'Удаление места...',
      success: (message) => message,
      error: (err) => `Ошибка: ${err.message}`,
    });
  }, []);

  const handleVoteOptimistic = useCallback((placeId: string, voteType: VoteType) => {
    setPlaces((current) =>
      current.map((p) => (p.id === placeId ? applyOptimisticVote(p, voteType) : p)),
    );

    // ✅ если открыт place не из списка — обновляем fallback
    setActiveFallback((cur) => (cur?.id === placeId ? applyOptimisticVote(cur, voteType) : cur));
  }, []);

  const handleVote = useCallback(
    async (placeId: string, voteType: VoteType) => {
      if (!userId) {
        toast.error('Войдите, чтобы голосовать');
        return;
      }

      handleVoteOptimistic(placeId, voteType);

      try {
        await voteOnPlace(placeId, voteType);
      } catch (error) {
        console.error('Ошибка голосования:', error);
        toast.error('Не удалось сохранить голос');
      }
    },
    [userId, handleVoteOptimistic],
  );

  const handleCloseModal = useCallback(() => {
    setActivePlaceId(null);
    setActiveFallback(null);
  }, []);

  // ✅ стабильный обработчик (один на все карточки)
  const handleViewDetails = useCallback((placeId: string, _scrollToComments = false) => {
    setActivePlaceId(placeId);
    setActiveFallback(null);
  }, []);

  const itemsWithBanner = useMemo(() => {
    if (!places?.length) return [];
    const items: (PlaceSeed | { type: 'banner' })[] = [...places];
    if (items.length > 2) items.splice(2, 0, { type: 'banner' });
    return items;
  }, [places]);

  // ✅ чтобы index для карточек не “съезжал” из-за баннера
  let placeIndex = -1;

  return (
    <>
      <div className="flex flex-col gap-12">
        {itemsWithBanner.map((item) => {
          if ('type' in item && item.type === 'banner') {
            return <InContentBanner key="in-content-banner" />;
          }

          const place = item as PlaceSeed;
          placeIndex += 1;

          return (
            <PlaceCard
              key={place.id}
              place={place}
              isAdmin={!!isAdmin}
              index={placeIndex}
              onDelete={handleDeletePlace}
              onVote={handleVote}
              onViewDetails={handleViewDetails} // ✅ больше не создаём функцию в map
            />
          );
        })}
      </div>

      <AnimatePresence>
        {activePlace && (
          <ItemViewModal
            key={activePlace.id}
            itemSeed={activePlace}
            itemType="place"
            onClose={handleCloseModal}
            scrollToCommentId={commentIdToScroll}
            onVoteOptimistic={handleVoteOptimistic}
          />
        )}
      </AnimatePresence>
    </>
  );
}
